/**
* MUI Door component that replaces the styled-components Door component
* 
* This component provides a Material UI equivalent of the original Door component
* with similar props and functionality.
*/
import React, { useState, useRef, useEffect, Fragment } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { getConditionResult, getCurrentValue2 } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox, getCurrentView } from "reduxModules/ducks/ui";
import { Move } from "globalConstants/Icons/Actions";
import { iconThemes } from "globalConstants/Themes";
import ReducersIndex from 'reduxModules/ducks';
import useMousePosition from "hooks/useMousePosition";
import { assignWith, isEmpty, isNull, mapValues, round } from "lodash";
import { 
  MuiDoorProps, 
  DoorSize, 
  DoorTransform, 
  DoorGroupProps, 
  DoorElementProps, 
  DoorMoveProps 
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * Standard door size constants
 */
const DEFAULT_DOOR_SIZE: DoorSize = {
  height: 0.9144,
  width: 0.03175,
  margin: 0.0254
};

/**
 * MUI Door component
 */
const MuiDoor: React.FC<MuiDoorProps> = (props) => {
  const areaId = useSelector((state: RootState) => getCurrentValue2(state, 'activeTab'));
  const { bbox, scale } = useSelector(getDrawingBox);
  const currentDoor = useSelector((state: RootState) => getCurrentValue2(state, 'door'));
  const currentView = useSelector(getCurrentView);
  const pxSize = mapValues(DEFAULT_DOOR_SIZE, (v) => v * scale);

  // Handle transformations 
  const dispatch = useDispatch();
  const [transform, setTransform] = useState<DoorTransform>({}); 
  const updateTransform = (args: DoorTransform) => setTransform(prevState => ({ ...prevState, ...args }));
  const [showDoor, setShowDoor] = useState<boolean>(false);

  useEffect(() => {
    if (!isEmpty(transform) && !isNull(transform.x) && !isNull(transform.y)) {
      dispatch((ReducersIndex.container as any).setDoor(
        assignWith(
          { ...transform }, 
          { x: bbox.x0, y: bbox.y0 }, 
          (a: number, b: number) => round((a - b) / scale, 4)
        )
      ));
    }
  }, [transform.x, transform.y, bbox.x0, bbox.y0, dispatch, scale]);

  useEffect(() => {
    let x: number, y: number;
    if (currentDoor.x === null || currentDoor.y === null) {
      x = bbox.x0 + pxSize.margin;
      y = bbox.y1 - pxSize.height - pxSize.margin;
      dispatch((ReducersIndex.container as any).setDoor({ 
        x: round((x - bbox.x0) / scale, 4), 
        y: round((y - bbox.y0) / scale, 4) 
      }));
    } else {
      setShowDoor(false);
      const door = {
        ...currentDoor,
        x: currentDoor.x * scale + bbox.x0,
        y: currentDoor.y * scale + bbox.y0
      };
      switch (door.side) {
        case 0:
          x = bbox.x0 + pxSize.height + pxSize.margin;
          y = Math.min(Math.max(bbox.y0 + pxSize.margin, door.y), bbox.y1 - pxSize.width - pxSize.margin);
          break;
        case 1:
          x = Math.min(Math.max(bbox.x0 + pxSize.margin, door.x), bbox.x1 - pxSize.width - pxSize.margin);
          y = bbox.y0 + pxSize.height + pxSize.margin;
          break;
        case 2:
          x = bbox.x1 - pxSize.height - pxSize.margin;
          y = Math.min(Math.max(bbox.y0 + pxSize.margin, door.y), bbox.y1 - pxSize.width - pxSize.margin);
          break;
        case 3:
          x = Math.min(Math.max(bbox.x0 + pxSize.margin, door.x), bbox.x1 - pxSize.width - pxSize.margin);
          y = bbox.y1 - pxSize.height - pxSize.margin;
          break;
        default:
          x = bbox.x0 + pxSize.margin;
          y = bbox.y1 - pxSize.height - pxSize.margin;
      }
      updateTransform({ ...door, x: x, y: y });
      setShowDoor(true);
    }
  }, [bbox.x0, bbox.y0, bbox.x1, bbox.y1, scale, currentDoor.x, currentDoor.y, dispatch, pxSize.height, pxSize.margin]);

  const mouse = useMousePosition(true, true);
  const [moving, setMoving] = useState<boolean>(false);
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!doorIconRef.current) return;
      doorIconRef.current.setPointerCapture(e.pointerId);
      
      // Get current mouse position in SVG coordinates
      if (mouse) {
        // Calculate offset from current door position
        setOffset({ 
          x: mouse.x - (transform.x || 0), 
          y: mouse.y - (transform.y || 0) 
        });
      } else {
        setOffset({ x: 0, y: 0 });
      }
      
      setMoving(true);
      dispatch(ReducersIndex.uiReducers.setAction('moveDoor'));
      e.stopPropagation();
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!moving) return;
      moveDoor();
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!doorIconRef.current) return;
      doorIconRef.current.releasePointerCapture(e.pointerId);
      setMoving(false);
      dispatch(ReducersIndex.uiReducers.setAction());
      dispatch((ReducersIndex.container as any).setDoor(
        assignWith(
          { ...transform }, 
          { x: bbox.x0, y: bbox.y0 }, 
          (a: number, b: number) => round((a - b) / scale, 4)
        )
      ));
    }
  };

  const moveDoor = () => {
    const { x: mouseX = 0, y: mouseY = 0 } = mouse ?? {};
    if (!mouseX || !mouseY) return;

    // Offset-adjusted coords for final placement on the wall
    const x = mouseX - offset.x;
    const y = mouseY - offset.y;

    const doorMargin = pxSize.margin;
    const doorThickness = pxSize.height;
    const cx = (bbox.x0 + bbox.x1) / 2;
    const cy = (bbox.y0 + bbox.y1) / 2;

    // Wall is determined by which axis the cursor is further from the room center.
    // This means the door only switches walls (and rotates) when crossing the center diagonals.
    const distFromCenterX = Math.abs(mouseX - cx);
    const distFromCenterY = Math.abs(mouseY - cy);

    let side: number, rotate: number;
    let finalX: number, finalY: number;

    const doorWidth = pxSize.width; // thin dimension (~3cm) — cross-axis extent of door panel

    if (distFromCenterX > distFromCenterY) {
      // Left or right wall — door is rotated -90°, so its cross-axis (Y) extent is doorWidth
      rotate = -90;
      if (mouseX < cx) {
        side = 0; // Left
        finalX = bbox.x0 + doorThickness + doorMargin;
      } else {
        side = 2; // Right
        finalX = bbox.x1 - doorThickness - doorMargin;
      }
      finalY = Math.min(Math.max(bbox.y0 + doorMargin, y), bbox.y1 - doorWidth - doorMargin);
    } else {
      // Top or bottom wall — not rotated, so its cross-axis (X) extent is doorWidth
      rotate = 0;
      if (mouseY < cy) {
        side = 1; // Top
        finalY = bbox.y0 + doorThickness + doorMargin;
      } else {
        side = 3; // Bottom
        finalY = bbox.y1 - doorThickness - doorMargin;
      }
      finalX = Math.min(Math.max(bbox.x0 + doorMargin, x), bbox.x1 - doorWidth - doorMargin);
    }

    // The door arc flips to open toward the nearest corner (back wall).
    // y-component: left/top walls use -1, right/bottom walls use +1.
    // x-component: on vertical walls flip based on top/bottom half of wall;
    //              on horizontal walls flip based on left/right half of wall.
    const yFlip: number = side < 2 ? -1 : 1;
    const xFlip: number = distFromCenterX > distFromCenterY
      ? (finalY < cy ? -1 : 1)   // vertical wall: top half opens down, bottom half opens up
      : (finalX < cx ? 1 : -1);  // horizontal wall: left half opens right, right half opens left
    const doorScale: [number, number] = [xFlip, yFlip];

    updateTransform({ side, x: finalX, y: finalY, scale: doorScale, angle: rotate });
  };

  const active = useSelector((state: RootState) => getConditionResult(state, { ...props.active, area: areaId }));
  const doorIconRef = useRef<SVGGElement>(null);

  return (
    <Fragment>
      {showDoor && (
        <DoorGroup 
          id={`${areaId}-door`} 
          active={active} 
          transform={`translate(${transform.x}, ${transform.y}) rotate(${transform.angle}) scale(${transform.scale?.join(',')}) `}
        >
          <DoorRect 
            width={DEFAULT_DOOR_SIZE.width * scale}
            height={DEFAULT_DOOR_SIZE.height * scale}
            x={2}
            y={2}
            active={active}
            currentView={currentView}
          />
          <DoorArc 
            d={`M${DEFAULT_DOOR_SIZE.width * scale}, 2a${(DEFAULT_DOOR_SIZE.height - DEFAULT_DOOR_SIZE.width) * scale - 2} ${(DEFAULT_DOOR_SIZE.height - DEFAULT_DOOR_SIZE.width) * scale - 2} 0 0 1 ${(DEFAULT_DOOR_SIZE.height - DEFAULT_DOOR_SIZE.width) * scale - 2} ${DEFAULT_DOOR_SIZE.height * scale - 2}`}
            active={active}
            currentView={currentView}
          />
          {active && (
            <DoorMove 
              className="action"
              ref={doorIconRef} 
              transform={`translate(${DEFAULT_DOOR_SIZE.height * scale * 0.25}, ${DEFAULT_DOOR_SIZE.height * scale * 0.75}) scale(${DEFAULT_DOOR_SIZE.height * scale * 0.006})`}
              pointer={moving} 
              {...handlers}
            >
              <Move state={iconThemes['door_move']} center />
            </DoorMove>
          )}
        </DoorGroup>
      )}
    </Fragment>
  );
};

// Styled components using MUI styled API
const DoorGroup = styled('g', {
  shouldForwardProp: (prop) => prop !== 'active',
})<DoorGroupProps>`
  fill: none;
  stroke-width: 2;
  shape-rendering: crispedges;
  pointer-events: ${({ active }) => active ? 'bounding-box' : 'none'};
`;

const DoorRect = styled('rect', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'currentView',
})<DoorElementProps>`
  stroke: ${({ active, currentView }) => (currentView === 'floor_plan' || currentView === 'floorplan') ? '#007bff' : '#6b7280'};
  fill: ${({ active, currentView }) => (currentView === 'floor_plan' || currentView === 'floorplan') ? '#007bff' : '#6b7280'};
`;

const DoorArc = styled('path', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'currentView',
})<DoorElementProps>`
  shape-rendering: geometricprecision;
  stroke: ${({ active, currentView }) => (currentView === 'floor_plan' || currentView === 'floorplan') ? '#007bff' : '#6b7280'};
  fill: none;
`;

const DoorMove = styled('g', {
  shouldForwardProp: (prop) => prop !== 'pointer',
})<DoorMoveProps>`
  cursor: ${({ pointer }) => pointer ? 'all-scroll' : 'pointer'};
  &:hover {
    cursor: pointer;
  }
`;

export default MuiDoor;
